import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileUploadModule } from 'primeng/fileupload';
import { CommonModule } from '@angular/common';
import { ActivityType } from '../../master-data/activity-type/activity-type.model';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { AppUser } from '../../users/user.model';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ClaimContractService } from '../claim-contract.service';
import { ActivityTypeService } from '../../master-data/activity-type/activity-type.service';
import { DiscountType } from '../duscount-types';
import { Dialog } from 'primeng/dialog';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import * as XLSX from 'xlsx';
import { ChatSessionService } from '../../services/chat-session.service';
import { ChatMessage } from '../../models/chat-message.model';

interface ClaimContractDetailFormValue {
  business: Business | null;
  supplier: Supplier | null;
  activityType: ActivityType | null;
  discountType: string | null;
  claimPercent: number | null;
  startDate: Date | null;
  endDate: Date | null;
  apGlSegment1: string | null;
  apGlSegment2: string | null;
  apGlSegment3: string | null;
  apGlSegment4: string | null;
  apGlSegment5: string | null;
  apGlSegment6: string | null;
  apGlSegment7: string | null;
  apGlSegment8: string | null;
  apGlSegment9: string | null;
  apGlSegment10: string | null;
}

@Component({
  selector: 'app-add-edit-contract',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    FieldsetModule,
    ButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    FileUploadModule,
    DatePicker,
    ProgressSpinner,
    ConfirmDialog,
    Dialog,
  ],
  providers: [MessageService],
  templateUrl: './claim-contract-form.component.html',
  styleUrls: ['./claim-contract-form.component.css'],
})
export class ClaimContractFormComponent implements OnInit, OnDestroy {
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;
  claimContractForm!: FormGroup;
  isLoading = false;
  submitted = false;
  isEditMode = false;
  claimContractId: string | null = null;
  currentUser: AppUser;

  // Modal state for per-row required fields
  showRowModal = false;
  modalRowIndex: number | null = null;
  rowModalForm!: FormGroup;

  // Breadcrumb items
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  filteredSuppliers: Supplier[] = [];

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];

  activityTypes: ActivityType[] = [];
  filteredActivityTypes: ActivityType[] = [];

  formInfoHolder: FormGroup[] = [];
  tomorrowDate!: Date;
  private valueChangesSub!: Subscription;

  suppliersPerRow: Supplier[][] = [];
  oldSuppliersPerRow: Supplier[][] = [];
  BusinessfilterValue: string[] = [];
  SupplierfilterValue: string[] = [];
  ActivityTypefilterValue: string[] = [];
  discountTypes: { label: string; value: string }[] = Object.entries(
    DiscountType
  ).map(([key, value]) => ({
    label: value,
    value: key,
  }));
  readonly claimImportAcceptTypes = '.xlsx,.xls';
  selectedImportFileName = '';
  private readonly importMessageLife = 15000;
  private readonly maxListedImportMessages = 10;
  // sender مش المستخدم الحالي عشان الرسالة تتعرض كرسالة بوت في الشات
  private readonly importIssuesSender = {
    id: -1,
    fullName: 'Tamkeen',
  } as AppUser;
  private readonly monthAbbreviations = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private businessService: BusinessService,
    private customerService: CustomersService,
    private route: ActivatedRoute,
    private claimContractService: ClaimContractService,
    private activityTypeService: ActivityTypeService,
    private chatSessionService: ChatSessionService
  ) {
    this.currentUser = JSON.parse(
      sessionStorage.getItem('appUser') ?? ''
    ) as AppUser;
  }

  ngOnInit(): void {
    this.getTomorrowDate();

    this.claimContractId = this.route.snapshot.paramMap.get('claimContractId');
    this.isEditMode = !!this.claimContractId;

    this.initForm();
    // this.subscribeToValueChanges();

    // Load all necessary dropdown data and then proceed with form population
    this.loadDropdownData().subscribe({
      next: (data) => {
        this.businesses = data.businesses.content;
        // this.suppliers = data.suppliers.content;
        this.filteredBusinesses = data.businesses.content;
        this.activityTypes = data.activityTypes.content;
        this.filteredActivityTypes = data.activityTypes.content;

        // Now that dropdowns are populated, we can safely check for pre-fill or edit mode.
        if (this.isEditMode) {
          this.claimContractService
            .getClaimContractById(+this.claimContractId!)
            .subscribe((claimContract) => {
              // Find the correct object references from the dropdown lists to ensure proper binding.
              // const customerToSet = this.customers.find(
              //   (c) => c.id === contractClaim.customer?.id
              // );
              // const businessToSet = this.businesses.find(
              //   (b) => b.id === contractClaim.business?.id
              // );
            });
        } else {
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load required form data.',
        });
        console.error('Error loading dropdown data:', err);
      },
    });

    // revalidate duplicates whenever form array changes
    this.subscribeToUniqueValidation();
  }

  private initForm(): void {
    this.claimContractForm = this.fb.group({
      claimContractDetails: this.fb.array([], Validators.required), // form array
    });
    this.addItemToClaimContractDetail();
  }

  validatePercentage(event: any, fieldName: string, index: number) {
    const value = Number(event.target.value);
    const detailArray = this.claimContractForm.get('claimContractDetails') as FormArray;
    detailArray.at(index).get(fieldName)?.reset();

    if (value < 0) {
      detailArray.at(index).get(fieldName)?.setValue(1);
    } else if (value > 100) {
      detailArray.at(index).get(fieldName)?.setValue(100);
    } else {
      detailArray.at(index).get(fieldName)?.setValue(value);
    }
  }

 isDetailsInvalid(index: number, controlName: string): boolean {
  const detailArray = this.claimContractForm.get('claimContractDetails') as FormArray;
  const control = detailArray.at(index).get(controlName);

  return !!(
    control &&
    control.invalid &&
    (control.dirty || control.touched || this.submitted)
  );
}
isRowModalFieldInvalid(controlName: string): boolean {
  const control = this.rowModalForm?.get(controlName);
  return !!(
    control &&
    control.invalid &&
    (control.dirty || control.touched)
  );
}

  get claimContractDetails(): FormArray {
    return this.claimContractForm.get('claimContractDetails') as FormArray;
  }

  addItemToClaimContractDetail() {
    const detail = this.createClaimContractDetailGroup();
    this.claimContractDetails.push(detail);
  }

  private createClaimContractDetailGroup(initialValue?: Partial<ClaimContractDetailFormValue>) {
    return this.fb.group({
      business: [initialValue?.business ?? null, Validators.required],
      supplier: [initialValue?.supplier ?? null, Validators.required],
      activityType: [initialValue?.activityType ?? null, Validators.required],
      discountType: [initialValue?.discountType ?? null, Validators.required],
      claimPercent: [
        initialValue?.claimPercent ?? null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      startDate: [initialValue?.startDate ?? null, Validators.required],
      endDate: [initialValue?.endDate ?? null],
      apGlSegment1: [initialValue?.apGlSegment1 ?? null, Validators.required],
      apGlSegment2: [initialValue?.apGlSegment2 ?? null, Validators.required],
      apGlSegment3: [initialValue?.apGlSegment3 ?? null, Validators.required],
      apGlSegment4: [initialValue?.apGlSegment4 ?? null, Validators.required],
      apGlSegment5: [initialValue?.apGlSegment5 ?? null, Validators.required],
      apGlSegment6: [initialValue?.apGlSegment6 ?? null, Validators.required],
      apGlSegment7: [initialValue?.apGlSegment7 ?? null, Validators.required],
      apGlSegment8: [initialValue?.apGlSegment8 ?? null, Validators.required],
      apGlSegment9: [initialValue?.apGlSegment9 ?? null, Validators.required],
      apGlSegment10: [initialValue?.apGlSegment10 ?? null, Validators.required],
    });
  }

  removeItemFromClaimContractDetail(index: number) {
    this.claimContractDetails.removeAt(index);
  }

  onClaimContractExcelSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.selectedImportFileName = file.name;

    if (!this.businesses.length || !this.activityTypes.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Upload unavailable',
        detail: 'Please wait until the dropdown data finishes loading, then try again.',
      });
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      try {
        const data = target?.result;
        if (!(data instanceof ArrayBuffer)) {
          throw new Error('Unable to read file content.');
        }

        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
          defval: null,
          raw: true,
        });

        if (!rows.length) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No data found',
            detail: 'The uploaded Excel file does not contain any data rows.',
          });
          input.value = '';
          return;
        }

        const pastDateEntries = this.collectPastDateEntries(rows);
        if (pastDateEntries.length) {
          console.warn('Claim import - dates rejected:', pastDateEntries);
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid dates',
            detail: 'The date must be after today.',
          });
          input.value = '';
          return;
        }

        const validationMessages: string[] = [];
        const importedRows = rows
          .map((row, index) => this.mapExcelRowToFormValue(row, index + 2, validationMessages))
          .filter((row): row is ClaimContractDetailFormValue => !!row);

        // أي صف متسكب => نوقف الرفع كله ونبعت ملف بكل المشاكل في الشات
        if (validationMessages.length) {
          console.warn('Claim import - rows skipped:', validationMessages);
          this.sendImportIssuesToChat(file.name, rows.length, validationMessages);
          this.messageService.add({
            severity: 'error',
            summary: 'Import cancelled',
            detail: `${validationMessages.length} row(s) have issues. Nothing was imported - the issues file was added to the chat.`,
            life: this.importMessageLife,
          });
          input.value = '';
          return;
        }

        if (!importedRows.length) {
          this.messageService.add({
            severity: 'error',
            summary: 'Import failed',
            detail: 'No valid rows were found in the uploaded file.',
            life: this.importMessageLife,
          });
          input.value = '';
          return;
        }

        this.replaceClaimContractDetails(importedRows);

        this.messageService.add({
          severity: 'success',
          summary: 'Import complete',
          detail: `${importedRows.length} row(s) imported into the form.`,
        });
      } catch (error) {
        console.error('Error importing claim contract Excel:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Import failed',
          detail: 'The Excel file could not be parsed. Please check the file format and try again.',
        });
      } finally {
        input.value = '';
      }
    };

    reader.readAsArrayBuffer(file);
  }

  downloadClaimContractTemplate(): void {
    const headers = [
      'Business Code',
      'Business',
      'Supplier Number',
      'Supplier',
      'Activity Type',
      'Discount Type',
      'Claim Percent',
      'Start Date',
      'End Date',
      'AP GL Segment 1',
      'AP GL Segment 2',
      'AP GL Segment 3',
      'AP GL Segment 4',
      'AP GL Segment 5',
      'AP GL Segment 6',
      'AP GL Segment 7',
      'AP GL Segment 8',
      'AP GL Segment 9',
      'AP GL Segment 10',
    ];
    const sampleRow = [
      '',
      '',
      '',
      '',
      '',
      'Claim',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claim Contract Template');
    XLSX.writeFile(workbook, 'claim_contract_upload_template.xlsx');
  }


  submitForm() {
    this.submitted = true;

    if (this.claimContractForm.valid) {
      this.claimContractDetails.controls.forEach(
        (control: AbstractControl, index: number) => {
          control.patchValue({
            startDate: this.formatDateToLocal(control.value.startDate),
            endDate: this.formatDateToLocal(control.value.endDate),
          });
        }
      );

      this.isLoading = true;
      const payload = this.claimContractForm.value.claimContractDetails.map(
        (item: any) => ({
          business: item.business,
          supplier: item.supplier,
          activityType: item.activityType,
          discountType: item.discountType,
          claimPercent: item.claimPercent,
          startDate: item.startDate,
          endDate: item.endDate,
          apGlSegment1: item.apGlSegment1,
          apGlSegment2: item.apGlSegment2,
          apGlSegment3: item.apGlSegment3,
          apGlSegment4: item.apGlSegment4,
          apGlSegment5: item.apGlSegment5,
          apGlSegment6: item.apGlSegment6,
          apGlSegment7: item.apGlSegment7,
          apGlSegment8: item.apGlSegment8,
          apGlSegment9: item.apGlSegment9,
          apGlSegment10: item.apGlSegment10,
        })
      );

      this.claimContractService.createClaimContract(payload).subscribe({
        next: (data) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Claim Contract saved successfully.',
          });
          setTimeout(
            () =>
              this.router.navigate([
                '/ai-studio/session/current',
                { outlets: { canvasOutlet: ['claim-contract', 'list'] } },
              ]),
            1500
          );
        },
        error: (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save contract.',
          });
          console.error('Error saving contract:', err);
        },
      });
    }else{
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields.',
      });
    }
  }

  // Modal handlers for per-row fields
  openRowModal(index: number) {
    this.modalRowIndex = index;
    const row = this.claimContractDetails.at(index) as FormGroup;
    this.rowModalForm = this.fb.group({
      apGlSegment1: [row.get('apGlSegment1')?.value || null, Validators.required],
      apGlSegment2: [row.get('apGlSegment2')?.value || null, Validators.required],
      apGlSegment3: [row.get('apGlSegment3')?.value || null, Validators.required],
      apGlSegment4: [row.get('apGlSegment4')?.value || null, Validators.required],
      apGlSegment5: [row.get('apGlSegment5')?.value || null, Validators.required],
      apGlSegment6: [row.get('apGlSegment6')?.value || null, Validators.required],
      apGlSegment7: [row.get('apGlSegment7')?.value || null, Validators.required],
      apGlSegment8: [row.get('apGlSegment8')?.value || null, Validators.required],
      apGlSegment9: [row.get('apGlSegment9')?.value || null, Validators.required],
      apGlSegment10: [row.get('apGlSegment10')?.value || null, Validators.required],
    });
    this.showRowModal = true;
  }

  confirmRowModal() {
    if (!this.rowModalForm || !this.rowModalForm.valid || this.modalRowIndex === null) {
      // mark all controls as touched and log validation errors to console
      this.rowModalForm?.markAllAsTouched();
      Object.keys(this.rowModalForm?.controls || {}).forEach((key) => {
        const control = this.rowModalForm.get(key);
      });

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all required fields in the modal.',
      });
      return;
    }

    const row = this.claimContractDetails.at(this.modalRowIndex) as FormGroup;
    row.patchValue({
      apGlSegment1: this.rowModalForm.value.apGlSegment1,
      apGlSegment2: this.rowModalForm.value.apGlSegment2,
      apGlSegment3: this.rowModalForm.value.apGlSegment3,
      apGlSegment4: this.rowModalForm.value.apGlSegment4,
      apGlSegment5: this.rowModalForm.value.apGlSegment5,
      apGlSegment6: this.rowModalForm.value.apGlSegment6,
      apGlSegment7: this.rowModalForm.value.apGlSegment7,
      apGlSegment8: this.rowModalForm.value.apGlSegment8,
      apGlSegment9: this.rowModalForm.value.apGlSegment9,
      apGlSegment10: this.rowModalForm.value.apGlSegment10,
    });
    this.closeRowModal();
  }

  closeRowModal() {
    this.showRowModal = false;
    this.modalRowIndex = null;
  }

  getTomorrowDate() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    // لازم يبقى 00:00 عشان p-datePicker بيقارن بالوقت مش باليوم،
    // وأي تاريخ منتصف الليل كان بيتمسح من الـ input
    tomorrow.setHours(0, 0, 0, 0);
    this.tomorrowDate = tomorrow;
  }

  onCancel(): void {
    //this.router.navigate(['/contracts']); // Or use Location.back()
  }

  private loadDropdownData() {
    return forkJoin({
      businesses: this.businessService.getAllBusinesss({
        page: 0,
        size: 10000,
        loadSuppliers: true,
      }),
      activityTypes: this.activityTypeService.getAllActivityTypes({
        page: 0,
        size: 1000,
      }),

      // suppliers: this.supplierService.getAllSuppliers({ page: 0, size: 1000 }),
    });
  }

  //   getDropdownLabel(item: any): string {
  //     return item?.name || item?.customerName || 'N/A';
  //   }

  filterActivityTypes(event: any, index: number) {
    this.ActivityTypefilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredActivityTypes = this.activityTypes;
    } else {
      this.filteredActivityTypes = this.activityTypes.filter((activityType) =>
        activityType.name
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
      );
    }
  }

  filterBusinesses(event: any, index: number) {
    this.BusinessfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(
        (business) =>
          business.businessName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (business.businessCode &&
            business.businessCode
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSuppliers(event: any, index: number) {
    this.SupplierfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.suppliersPerRow[index] = this.oldSuppliersPerRow[index];
    } else {
      this.suppliersPerRow[index] = this.suppliersPerRow[index].filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplier.supplierNumber &&
            supplier.supplierNumber
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  ngOnDestroy(): void {
    // if (this.valueChangesSub) {
    //   this.valueChangesSub.unsubscribe();
    // }
  }

  get attachments(): FormArray {
    return this.claimContractForm.get('attachments') as FormArray;
  }

  onFileSelect(event: any): void {
    for (const file of event.files) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        // The result includes the mime type prefix, e.g., "data:image/png;base64,"
        const base64String = e.target.result;
        this.attachments.push(
          this.fb.group({
            fileName: [file.name],
            fileType: [file.type],
            fileSize: [file.size],
            fileContent: [base64String], // This is the Base64 string
          })
        );
      };
      reader.readAsDataURL(file);
    }
  }

  removeAttachment(index: number): void {
    this.attachments.removeAt(index);
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private replaceClaimContractDetails(rows: ClaimContractDetailFormValue[]) {
    while (this.claimContractDetails.length) {
      this.claimContractDetails.removeAt(0);
    }

    this.suppliersPerRow = [];
    this.oldSuppliersPerRow = [];
    this.BusinessfilterValue = [];
    this.SupplierfilterValue = [];
    this.ActivityTypefilterValue = [];

    rows.forEach((row, index) => {
      const detail = this.createClaimContractDetailGroup(row);
      this.claimContractDetails.push(detail);
      this.suppliersPerRow[index] = row.business?.suppliers ?? [];
      this.oldSuppliersPerRow[index] = row.business?.suppliers ?? [];
    });

    this.updateUniqueValidation();
  }

  private collectPastDateEntries(
    rows: Record<string, unknown>[]
  ): { row: number; column: string; raw: unknown; parsed: string }[] {
    const entries: { row: number; column: string; raw: unknown; parsed: string }[] =
      [];

    rows.forEach((row, index) => {
      if (this.isExcelRowEmpty(row)) {
        return;
      }

      const columns: [string, string[]][] = [
        ['Start Date', ['start date', 'start']],
        ['End Date', ['end date', 'end']],
      ];

      columns.forEach(([column, aliases]) => {
        const rawValue = this.getExcelCellValue(row, aliases);
        const parsedDate = this.parseExcelDate(rawValue);

        if (parsedDate && !this.isDateAfterToday(parsedDate)) {
          entries.push({
            row: index + 2,
            column,
            raw: rawValue,
            parsed: parsedDate.toString(),
          });
        }
      });
    });

    return entries;
  }

  private isDateAfterToday(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const candidate = new Date(date);
    candidate.setHours(0, 0, 0, 0);

    return candidate.getTime() > today.getTime();
  }

  private sendImportIssuesToChat(
    sourceFileName: string,
    totalRows: number,
    issues: string[]
  ): void {
    const issuesFileName = `claim-contract-import-issues-${this.formatTimestampForFileName(
      new Date()
    )}.txt`;
    const fileContent = this.buildImportIssuesFile(
      sourceFileName,
      totalRows,
      issues
    );

    const sessionId = this.resolveChatSessionId();
    if (sessionId === null || !this.isSessionLoaded(sessionId)) {
      // مفيش شات مفتوح => على الأقل ننزّل الملف للمستخدم
      this.downloadTextFile(issuesFileName, fileContent);
      return;
    }

    const message = new ChatMessage(
      `Upload of "${sourceFileName}" was cancelled. ${issues.length} row(s) have issues and no rows were imported. The full list is attached.`,
      this.importIssuesSender,
      sessionId
    );
    // ChatMessage بيبدأ isPartial = true، والشات بيفضل يعرض اللودر
    // طول ما آخر رسالة partial، فلازم نقفلها هنا
    message.isPartial = false;
    message.fileNameContent = issuesFileName;
    message.fileUrlContent = this.toTextDataUrl(fileContent);

    this.chatSessionService.addReceivedChatMessageToSession(message);
  }

  private resolveChatSessionId(): number | null {
    const activeSessionId = this.chatSessionService.getActiveSessionIdSnapshot();
    if (activeSessionId !== null) {
      return activeSessionId;
    }

    // fallback على الـ URL: /ai-studio/session/<id>/(canvasOutlet:...)
    const match = /\/session\/(\d+)/.exec(this.router.url);

    return match ? Number(match[1]) : null;
  }

  private isSessionLoaded(sessionId: number): boolean {
    // الرسالة بتتضاف للسيشن اللي في الميموري بس، فلو مش موجودة ننزّل الملف بدل ما تضيع
    let isLoaded = false;
    this.chatSessionService.sessions$
      .pipe(take(1))
      .subscribe((sessions) => {
        isLoaded = sessions.some((session) => session.sessionId === sessionId);
      });

    return isLoaded;
  }

  private buildImportIssuesFile(
    sourceFileName: string,
    totalRows: number,
    issues: string[]
  ): string {
    const lines = [
      'Claim Contract - Upload Issues',
      '==============================',
      `File        : ${sourceFileName}`,
      `Generated   : ${new Date().toLocaleString()}`,
      `Rows read   : ${totalRows}`,
      `Rows with issues: ${issues.length}`,
      '',
      'No rows were imported. Fix the rows below and upload the file again.',
      '',
      ...issues.map((issue, index) => `${index + 1}. ${issue}`),
      '',
    ];

    return lines.join('\r\n');
  }

  private toTextDataUrl(content: string): string {
    const base64 = btoa(
      String.fromCharCode(...new TextEncoder().encode(content))
    );

    return `data:text/plain;charset=utf-8;base64,${base64}`;
  }

  private downloadTextFile(fileName: string, content: string): void {
    const link = document.createElement('a');
    link.href = this.toTextDataUrl(content);
    link.download = fileName;
    link.click();
    link.remove();
  }

  private formatTimestampForFileName(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');

    return (
      `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
      `-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    );
  }

  private formatRowMessages(messages: string[]): string {
    if (messages.length === 1) {
      return messages[0];
    }

    const listed = messages.slice(0, this.maxListedImportMessages);
    const remaining = messages.length - listed.length;
    const formatted = listed.map((message) => `• ${message}`).join('\n');

    return remaining > 0
      ? `${formatted}\n• ...and ${remaining} more.`
      : formatted;
  }

  private mapExcelRowToFormValue(
    row: Record<string, unknown>,
    excelRowNumber: number,
    validationMessages: string[]
  ): ClaimContractDetailFormValue | null {
    if (this.isExcelRowEmpty(row)) {
      return null;
    }

    // مش محتاجين اسم الـ business عشان نلاقيه - بنعتمد على الكود، ولو الكود
    // مكرر بين أكتر من business بنستخدم كود السبلاير عشان نعرف مين بالظبط
    const businessCodeValue = this.getExcelCellValue(row, ['business code']);
    const businessNameValue = this.getExcelCellValue(row, ['business']);
    const supplierValue = this.getExcelCellValue(row, [
      'supplier number',
      'supplier',
      'supplier name',
    ]);

    const { business, reason: businessNotFoundReason } = this.resolveBusiness(
      businessCodeValue,
      businessNameValue,
      supplierValue
    );

    if (!business) {
      validationMessages.push(
        `Row ${excelRowNumber}: Business not found (${businessNotFoundReason}; ${this.describeLookupValues(
          [
            ['code', businessCodeValue],
            ['name', businessNameValue],
            ['supplier', supplierValue],
          ]
        )}).`
      );
      return null;
    }

    const supplier = this.findSupplier(business, supplierValue);
    if (!supplier) {
      validationMessages.push(`Row ${excelRowNumber}: Supplier not found for business ${business.businessCode ?? business.businessName}.`);
      return null;
    }

    const activityTypeValue = this.getExcelCellValue(row, [
      'activity type code',
      'activity type',
      'activity',
    ]);
    const activityType = this.findActivityType(activityTypeValue);
    if (!activityType) {
      validationMessages.push(`Row ${excelRowNumber}: Activity Type not found.`);
      return null;
    }

    const discountTypeValue = this.getExcelCellValue(row, ['discount type']);
    const discountType = this.findDiscountType(discountTypeValue);
    if (!discountType) {
      validationMessages.push(`Row ${excelRowNumber}: Discount Type not found.`);
      return null;
    }

    const claimPercentValue = this.getExcelCellValue(row, [
      'claim percent',
      'claim percent %',
      'claim %',
    ]);
    const claimPercent = this.parseClaimPercent(claimPercentValue);
    if (claimPercent === null) {
      validationMessages.push(`Row ${excelRowNumber}: Claim Percent is invalid.`);
      return null;
    }

    const startDate = this.parseExcelDate(
      this.getExcelCellValue(row, ['start date', 'start'])
    );
    if (!startDate) {
      validationMessages.push(`Row ${excelRowNumber}: Start Date is invalid.`);
      return null;
    }

    const endDate = this.parseExcelDate(
      this.getExcelCellValue(row, ['end date', 'end'])
    );

    const apGlSegments = Array.from({ length: 10 }, (_, segmentIndex) => {
      const segmentNumber = segmentIndex + 1;
      const value = this.getExcelCellValue(row, [
        `ap gl segment ${segmentNumber}`,
        `gl segment ${segmentNumber}`,
        `apglsegment${segmentNumber}`,
        `glsegment${segmentNumber}`,
      ]);

      return value === null || value === undefined ? null : String(value).trim();
    });

    const missingSegment = apGlSegments.findIndex((value) => !value);
    if (missingSegment >= 0) {
      validationMessages.push(
        `Row ${excelRowNumber}: AP GL Segment ${missingSegment + 1} is required.`
      );
      return null;
    }

    return {
      business,
      supplier,
      activityType,
      discountType,
      claimPercent,
      startDate,
      endDate,
      apGlSegment1: apGlSegments[0],
      apGlSegment2: apGlSegments[1],
      apGlSegment3: apGlSegments[2],
      apGlSegment4: apGlSegments[3],
      apGlSegment5: apGlSegments[4],
      apGlSegment6: apGlSegments[5],
      apGlSegment7: apGlSegments[6],
      apGlSegment8: apGlSegments[7],
      apGlSegment9: apGlSegments[8],
      apGlSegment10: apGlSegments[9],
    };
  }

  private isExcelRowEmpty(row: Record<string, unknown>): boolean {
    return Object.values(row).every((value) => {
      if (value === null || value === undefined) {
        return true;
      }

      return String(value).trim().length === 0;
    });
  }

  private getExcelCellValue(
    row: Record<string, unknown>,
    aliases: string[]
  ): unknown {
    const normalizedEntries = Object.entries(row).map(([key, value]) => [
      this.normalizeExcelHeader(key),
      value,
    ] as const);

    for (const alias of aliases) {
      const normalizedAlias = this.normalizeExcelHeader(alias);
      const match = normalizedEntries.find(([key]) => key === normalizedAlias);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  private normalizeExcelHeader(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // fallback لما الكود لوحده بيرجع أكتر من business: نضيّق بقايمة الأكواد
  // المتطابقة، وبعدين نلاقي مين فيهم فعلاً معاه السبلاير ده في قايمة السبلايرز بتاعته
  // بيدور بالكود بس - مش محتاج اسم الـ business خالص. لو الكود اتكرر بين أكتر
  // من business، بنستخدم كود السبلاير عشان نعرف مين فيهم بالظبط
  private resolveBusiness(
    codeValue: unknown,
    nameValue: unknown,
    supplierValue: unknown
  ): { business: Business | null; reason: string } {
    const code = this.resolveBusinessCode(codeValue, nameValue);

    if (!code) {
      // مفيش كود خالص - آخر حل نجرب بالاسم لوحده لو موجود ومطابق business واحد بالظبط
      const name = this.normalizeLookupValue(nameValue);
      if (!name) {
        return { business: null, reason: 'Business Code is required' };
      }

      const nameMatches = this.businesses.filter(
        (business) => this.normalizeLookupValue(business.businessName) === name
      );

      return nameMatches.length === 1
        ? { business: nameMatches[0], reason: '' }
        : {
            business: null,
            reason:
              nameMatches.length > 1
                ? 'Business matches multiple businesses - provide Business Code'
                : 'no matching business',
          };
    }

    const candidates = this.businesses.filter(
      (business) => this.normalizeLookupValue(business.businessCode) === code
    );

    if (!candidates.length) {
      return { business: null, reason: `no business found with code "${code}"` };
    }

    if (candidates.length === 1) {
      return { business: candidates[0], reason: '' };
    }

    // الكود مكرر بين أكتر من business - لازم كود السبلاير عشان نعرف مين بالظبط
    const supplierKey = this.normalizeLookupValue(supplierValue);
    if (!supplierKey) {
      return {
        business: null,
        reason: `Business Code "${code}" matches ${candidates.length} businesses - provide Supplier Number to identify which one`,
      };
    }

    const supplierMatches = candidates.filter(
      (business) => this.findSupplier(business, supplierValue) !== null
    );

    if (supplierMatches.length === 1) {
      return { business: supplierMatches[0], reason: '' };
    }

    return {
      business: null,
      reason:
        supplierMatches.length === 0
          ? `Business Code "${code}" matches ${candidates.length} businesses, and none of them has a supplier matching "${String(supplierValue).trim()}"`
          : `Business Code "${code}" and Supplier "${String(supplierValue).trim()}" still match ${supplierMatches.length} businesses`,
    };
  }

  private resolveBusinessCode(codeValue: unknown, nameValue: unknown): string {
    const code = this.normalizeLookupValue(codeValue);
    if (code) {
      return code;
    }

    // لو عمود الكود فاضي بس عمود التاني فيه صيغة مدمجة "كود - اسم" زي اللي
    // بيظهر في الـ dropdown ("3008 - ADIDAS")، نطلع الكود منها بس
    const combined = this.splitCombinedBusinessValue(nameValue);

    return combined?.code ?? '';
  }

  private splitCombinedBusinessValue(
    value: unknown
  ): { code: string; name: string } | null {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return null;
    }

    const match = /^([A-Za-z0-9]+)\s*[-–:]\s*(.+)$/.exec(raw);
    if (!match) {
      return null;
    }

    return {
      code: this.normalizeLookupValue(match[1]),
      name: this.normalizeLookupValue(match[2]),
    };
  }

  private describeLookupValues(values: [string, unknown][]): string {
    const described = values
      .filter(([, value]) => this.normalizeLookupValue(value).length > 0)
      .map(([label, value]) => `${label}: "${String(value).trim()}"`);

    return described.length ? described.join(', ') : 'both cells are empty';
  }

  private findSupplier(business: Business, value: unknown): Supplier | null {
    const normalizedValue = this.normalizeLookupValue(value);
    if (!normalizedValue) {
      return null;
    }

    return (
      (business.suppliers ?? []).find(
        (supplier) =>
          this.normalizeLookupValue(supplier.supplierNumber) === normalizedValue ||
          this.normalizeLookupValue(supplier.supplierName) === normalizedValue
      ) ?? null
    );
  }

  private findActivityType(value: unknown): ActivityType | null {
    const normalizedValue = this.normalizeLookupValue(value);
    if (!normalizedValue) {
      return null;
    }

    return (
      this.activityTypes.find(
        (activityType) =>
          this.normalizeLookupValue(activityType.code) === normalizedValue ||
          this.normalizeLookupValue(activityType.name) === normalizedValue
      ) ?? null
    );
  }

  private findDiscountType(value: unknown): string | null {
    const normalizedValue = this.normalizeLookupValue(value);
    if (!normalizedValue) {
      return null;
    }

    const matchedDiscountType = this.discountTypes.find(
      (discountType) =>
        this.normalizeLookupValue(discountType.value) === normalizedValue ||
        this.normalizeLookupValue(discountType.label) === normalizedValue
    );

    return matchedDiscountType?.value ?? null;
  }

  private normalizeLookupValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim().toLowerCase();
  }

  private parseClaimPercent(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue =
      typeof value === 'number'
        ? value
        : Number(String(value).replace('%', '').trim());

    if (Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
      return null;
    }

    return parsedValue;
  }

  private parseExcelDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : this.toCalendarDate(value);
    }

    if (typeof value === 'number') {
      const parsedDate = XLSX.SSF.parse_date_code(value);
      if (!parsedDate) {
        return null;
      }

      return new Date(
        parsedDate.y,
        parsedDate.m - 1,
        parsedDate.d,
        parsedDate.H,
        parsedDate.M,
        parsedDate.S
      );
    }

    const normalizedValue = String(value).trim();

    return normalizedValue ? this.parseDateText(normalizedValue) : null;
  }

  private toCalendarDate(value: Date): Date {
    // SheetJS ساعات بيرجع التاريخ ناقص أجزاء من الثانية (23:59:59.999)
    // أو مسجل على UTC، والاتنين بيزحلقوا اليوم يوم كامل
    const rounded = new Date(Math.round(value.getTime() / 60000) * 60000);
    const isUtcMidnight =
      rounded.getUTCHours() === 0 && rounded.getUTCMinutes() === 0;

    return isUtcMidnight
      ? new Date(
          rounded.getUTCFullYear(),
          rounded.getUTCMonth(),
          rounded.getUTCDate()
        )
      : new Date(
          rounded.getFullYear(),
          rounded.getMonth(),
          rounded.getDate()
        );
  }

  private parseDateText(value: string): Date | null {
    const datePart = value.split(/[T\s]/)[0];
    const tokens = datePart.split(/[-/.]/).filter((token) => token.length);

    if (tokens.length === 3) {
      const [first, second, third] = tokens;
      const isNumeric = (token: string) => /^\d+$/.test(token);

      // yyyy-mm-dd / yyyy-MMM-dd
      if (/^\d{4}$/.test(first)) {
        const month = isNumeric(second)
          ? Number(second)
          : this.getMonthFromName(second);
        return this.buildDate(Number(first), month, Number(third));
      }

      // dd-MMM-yy / dd-MMM-yyyy (e.g. 22-Sep-26)
      if (!isNumeric(second)) {
        return this.buildDate(
          this.expandYear(Number(third)),
          this.getMonthFromName(second),
          Number(first)
        );
      }

      // all numeric: day-first unless the parts can only be read month-first
      if (isNumeric(first) && isNumeric(third)) {
        const firstNumber = Number(first);
        const secondNumber = Number(second);
        const isDayFirst = firstNumber > 12 || secondNumber <= 12;

        return this.buildDate(
          this.expandYear(Number(third)),
          isDayFirst ? secondNumber : firstNumber,
          isDayFirst ? firstNumber : secondNumber
        );
      }
    }

    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private getMonthFromName(value: string): number {
    return (
      this.monthAbbreviations.indexOf(value.slice(0, 3).toLowerCase()) + 1
    );
  }

  private expandYear(year: number): number {
    if (year >= 100) {
      return year;
    }

    // نفس قاعدة Excel: 00-29 => 2000s, 30-99 => 1900s
    return year < 30 ? 2000 + year : 1900 + year;
  }

  private buildDate(year: number, month: number, day: number): Date | null {
    if (!year || !month || !day || month < 1 || month > 12) {
      return null;
    }

    const date = new Date(year, month - 1, day);

    // يرفض التواريخ غير الموجودة زي 31-Feb
    return date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
      ? date
      : null;
  }

  // --- cross-row uniqueness: business + supplier + activityType ---
  private subscribeToUniqueValidation() {
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
    }
    this.valueChangesSub = this.claimContractDetails.valueChanges.subscribe(() => {
      this.updateUniqueValidation();
    });
  }

  private updateUniqueValidation() {
    const details = this.claimContractDetails;
    const keyMap = new Map<string, number[]>();

    details.controls.forEach((ctrl, i) => {
      const businessId: number | null = ctrl.get('business')?.value?.id ?? null;
      const supplierId: number | null = ctrl.get('supplier')?.value?.id ?? null;
      const activityTypeId: number | null = ctrl.get('activityType')?.value?.id ?? null;
      const key = `${businessId ?? ''}|${supplierId ?? ''}|${activityTypeId ?? ''}`;
      if (!keyMap.has(key)) keyMap.set(key, []);
      keyMap.get(key)!.push(i);
    });

    // clear previous duplicate errors
    details.controls.forEach((ctrl) => {
      ['business', 'supplier', 'activityType'].forEach((name) => {
        const c = ctrl.get(name);
        if (c) {
          const errs = { ...(c.errors || {}) };
          if ('duplicate' in errs) {
            delete errs['duplicate'];
            c.setErrors(Object.keys(errs).length ? errs : null);
          }
        }
      });
    });

    let hasDuplicates = false;
    keyMap.forEach((indices) => {
      if (indices.length > 1) {
        hasDuplicates = true;
        indices.forEach((idx) => {
          const row = details.at(idx);
          ['business', 'supplier', 'activityType'].forEach((name) => {
            const c = row.get(name);
            if (c) {
              const errs = { ...(c.errors || {}) };
              errs['duplicate'] = true;
              c.setErrors(errs);
            }
          });
        });
      }
    });

    // set or clear array-level error for duplicates
    if (hasDuplicates) {
      const errs = { ...(details.errors || {}) };
      errs['duplicateEntries'] = true;
      details.setErrors(errs);
    } else {
      const errs = { ...(details.errors || {}) };
      if ('duplicateEntries' in errs) {
        delete errs['duplicateEntries'];
      }
      details.setErrors(Object.keys(errs).length ? errs : null);
    }
  }

   onBusinessChange(index: number, business: Business) {
    if (this.suppliersPerRow.length <= index) {
      this.suppliersPerRow.push([]);
    }
    this.suppliersPerRow[index] = business.suppliers ?? [];
    this.oldSuppliersPerRow[index] = business.suppliers ?? [];

    const detailArray = this.claimContractForm.get('claimContractDetails') as FormArray;
    detailArray.at(index).get('supplier')?.reset();

  if (this.suppliersPerRow[index].length === 1) {
    detailArray
      .at(index)
      .get('supplier')
      ?.setValue(this.suppliersPerRow[index][0]);
  }

    // revalidate uniqueness after business changes
    this.updateUniqueValidation();
  }
}
